/**
 * @fileoverview Legacy migration grid component matching standard platform DataGrid layout, toolbar, column filters, and query builder.
 * @module system-web/components/migration/LegacyMigrationGrid
 *
 * Responsibilities:
 * - Render scored grid of legacy SQL rows matching platform DataGrid styling and toolbar structure (Filters, Query, Column Settings, Export).
 * - Support column-level filter inputs directly beneath table header (toggled by Filters button).
 * - Support Query Builder modal for complex AND/OR filter conditions (toggled by Query button).
 * - Support Column Settings popover for toggling column visibility on/off.
 * - Support Export popover for downloading CSV or JSON.
 * - Provide a page header checkbox plus a second "Select all N records" control for the full server total.
 * - Render server-side pagination controls matching the platform DataGrid pattern.
 * - Full Screen is owned by LegacyMigrationPage so the page header stays visible.
 *
 * Dependencies:
 * - lucide-react (icons)
 * - useLegacyMigrationGrid hook
 */

import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import {
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ArrowUpDown, ArrowUp, ArrowDown, Search, X, Eye, EyeOff,
  SlidersHorizontal, Settings2, Download, FileText, FileSpreadsheet,
  Zap, Plus, Trash2, Filter, Maximize2, Minimize2,
} from 'lucide-react';
import type { ScoredGridRow } from '../../services/legacyMigrationService';
import { shouldOfferDatasetSelect, togglePageIds } from '../../services/legacyMigrationSelection';
import {
  getResolvedCellValue,
  isQueryGroup,
  type QueryCondition,
  type QueryGroup,
  type QueryOperator,
} from '../../services/legacyMigrationGridFilter';
import { DocumentHealthCheckButton } from './DocumentHealthCheckButton';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export interface GridColumnDef {
  key: string;
  header: string;
  width?: number;
}

export interface LegacyMigrationGridProps {
  datasetKey?: string;
  rows: ScoredGridRow[];
  columns: GridColumnDef[];
  isFetching: boolean;
  fetchError: string | null;
  refetch: () => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  selectionType?: 'explicit' | 'all';
  onSelectionTypeChange?: (type: 'explicit' | 'all') => void;
  excludedIds?: Set<string>;
  onExcludedIdsChange?: (ids: Set<string>) => void;
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  bandFilter?: string;
  onBandFilterChange?: (band: string) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (columnKey: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  columnFilters?: Record<string, string>;
  onColumnFiltersChange?: (filters: Record<string, string>) => void;
  queryJson?: string;
  onQueryJsonChange?: (query: string) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

// ---------------------------------------------------------------------------
// Query Builder Types
// ---------------------------------------------------------------------------

function emptyCondition(field: string): QueryCondition {
  return { id: String(Math.random()), field, operator: 'contains', value: '' };
}

function emptyGroup(): QueryGroup {
  return { id: String(Math.random()), logic: 'AND', conditions: [] };
}

function parseQueryGroup(raw?: string): QueryGroup {
  if (!raw?.trim()) return emptyGroup();
  try {
    const parsed = JSON.parse(raw) as QueryGroup;
    if (parsed && Array.isArray(parsed.conditions)) return parsed;
  } catch {
    /* keep empty group */
  }
  return emptyGroup();
}

// ---------------------------------------------------------------------------
// Constants & Helpers
// ---------------------------------------------------------------------------

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const BAND_CONFIG = {
  green:   { icon: CheckCircle2,   color: '#16a34a', bg: 'rgba(22,163,74,0.1)',   label: 'Green'   },
  yellow:  { icon: AlertTriangle,  color: '#d97706', bg: 'rgba(217,119,6,0.1)',   label: 'Yellow'  },
  red:     { icon: XCircle,        color: '#dc2626', bg: 'rgba(220,38,38,0.1)',   label: 'Red'     },
  blocked: { icon: XCircle,        color: '#7c3aed', bg: 'rgba(124,58,237,0.1)', label: 'Blocked' },
} as const;

function BandBadge({ band }: { band: ScoredGridRow['band'] }) {
  const cfg = BAND_CONFIG[band] ?? BAND_CONFIG.red;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold shrink-0"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function exportToCSV(filename: string, columns: GridColumnDef[], rows: ScoredGridRow[]) {
  const headers = ['Score', 'Band', ...columns.map((c) => c.header)];
  const csvRows = [headers.join(',')];
  for (const r of rows) {
    const values = [
      r.score,
      r.band,
      ...columns.map((c) => {
        const val = r.cells[c.key];
        if (val == null) return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
      }),
    ];
    csvRows.push(values.join(','));
  }
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function exportToJSON(filename: string, rows: ScoredGridRow[]) {
  const jsonStr = JSON.stringify(rows.map((r) => ({ score: r.score, band: r.band, ...r.cells })), null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.json`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ---------------------------------------------------------------------------
// Query Builder Modal Sub-Component
// ---------------------------------------------------------------------------

function QueryBuilderModal({
  columns,
  query,
  onApply,
  onClose,
}: {
  columns: GridColumnDef[];
  query: QueryGroup;
  onApply: (q: QueryGroup) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<QueryGroup>(query);

  const firstField = columns[0]?.key || 'code';

  const addCondition = () => {
    setDraft((prev) => ({
      ...prev,
      conditions: [...prev.conditions, emptyCondition(firstField)],
    }));
  };

  const removeCondition = (idx: number) => {
    setDraft((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== idx),
    }));
  };

  const updateCondition = (idx: number, updated: QueryCondition) => {
    setDraft((prev) => {
      const next = [...prev.conditions];
      next[idx] = updated;
      return { ...prev, conditions: next };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Zap className="h-4 w-4 text-orange-600" />
              Query Builder
            </h3>
            <p className="text-xs text-gray-500">Build custom AND/OR filter conditions for legacy dataset rows</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto space-y-3">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-700">
            <span>Match</span>
            <select
              value={draft.logic}
              onChange={(e) => setDraft({ ...draft, logic: e.target.value as 'AND' | 'OR' })}
              className="px-2 py-1 border border-gray-300 rounded font-bold text-orange-700 bg-orange-50 focus:ring-1 focus:ring-orange-500"
            >
              <option value="AND">ALL (AND)</option>
              <option value="OR">ANY (OR)</option>
            </select>
            <span>of the following conditions:</span>
          </div>

          {draft.conditions.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg text-xs text-gray-500">
              No query conditions added yet. Click <strong>Add Condition</strong> below to start filtering.
            </div>
          ) : (
            draft.conditions.map((item, idx) => {
              if (isQueryGroup(item)) return null;
              return (
                <div key={item.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs">
                  <select
                    value={item.field}
                    onChange={(e) => updateCondition(idx, { ...item, field: e.target.value })}
                    className="px-2 py-1.5 border border-gray-300 rounded bg-white font-medium min-w-[130px]"
                  >
                    {columns.map((c) => (
                      <option key={c.key} value={c.key}>{c.header}</option>
                    ))}
                  </select>

                  <select
                    value={item.operator}
                    onChange={(e) => updateCondition(idx, { ...item, operator: e.target.value as QueryOperator })}
                    className="px-2 py-1.5 border border-gray-300 rounded bg-white min-w-[120px]"
                  >
                    <option value="contains">contains</option>
                    <option value="notContains">does not contain</option>
                    <option value="equals">equals</option>
                    <option value="notEquals">does not equal</option>
                    <option value="startsWith">starts with</option>
                    <option value="endsWith">ends with</option>
                    <option value="isEmpty">is empty</option>
                    <option value="isNotEmpty">is not empty</option>
                  </select>

                  {item.operator !== 'isEmpty' && item.operator !== 'isNotEmpty' && (
                    <input
                      type="text"
                      value={item.value}
                      onChange={(e) => updateCondition(idx, { ...item, value: e.target.value })}
                      placeholder="Value..."
                      className="px-2.5 py-1.5 border border-gray-300 rounded bg-white flex-1 min-w-0"
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => removeCondition(idx)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                    title="Remove condition"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })
          )}

          <button
            type="button"
            onClick={addCondition}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Condition
          </button>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50/50 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setDraft(emptyGroup())}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
          >
            Clear All Conditions
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                onApply(draft);
                onClose();
              }}
              className="px-4 py-1.5 text-xs font-semibold bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors shadow-xs"
            >
              Apply Query
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function LegacyMigrationGrid({
  datasetKey = 'legacy-dataset',
  rows,
  columns,
  isFetching,
  fetchError,
  refetch,
  selectedIds,
  onSelectionChange,
  selectionType = 'explicit',
  onSelectionTypeChange,
  excludedIds = new Set(),
  onExcludedIdsChange,
  total,
  page,
  pageSize,
  pageCount,
  onPageChange,
  onPageSizeChange,
  bandFilter = 'all',
  onBandFilterChange,
  sortBy,
  sortDir = 'asc',
  onSortChange,
  searchQuery = '',
  onSearchChange,
  columnFilters,
  onColumnFiltersChange,
  queryJson,
  onQueryJsonChange,
  isFullscreen = false,
  onToggleFullscreen,
}: LegacyMigrationGridProps) {
  const [viewRow, setViewRow] = useState<ScoredGridRow | null>(null);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<string>>(new Set());

  // Filter Row State (toggled by "Filters" button)
  const [showFilterRow, setShowFilterRow] = useState(false);
  const [localColFilters, setLocalColFilters] = useState<Record<string, string>>({});
  const colFilters = columnFilters ?? localColFilters;
  const setColFilters = onColumnFiltersChange ?? setLocalColFilters;

  // Query Builder State (toggled by "Query" button)
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [localQueryGroup, setLocalQueryGroup] = useState<QueryGroup>(emptyGroup());
  const queryGroup = onQueryJsonChange ? parseQueryGroup(queryJson) : localQueryGroup;
  const setQueryGroup = (next: QueryGroup) => {
    if (onQueryJsonChange) {
      onQueryJsonChange(next.conditions.length > 0 ? JSON.stringify(next) : '');
      return;
    }
    setLocalQueryGroup(next);
  };

  // Popover toggle states
  const [showColumnSettingsPopover, setShowColumnSettingsPopover] = useState(false);
  const [showExportPopover, setShowExportPopover] = useState(false);

  const colSettingsRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  // Close popovers on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (colSettingsRef.current && !colSettingsRef.current.contains(e.target as Node)) {
        setShowColumnSettingsPopover(false);
      }
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportPopover(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenColumnKeys.has(c.key)),
    [columns, hiddenColumnKeys],
  );

  const toggleColumnVisibility = (key: string) => {
    setHiddenColumnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Sub-Category Filter State (e.g. Backup Power, Fire Alarm, Fire Pump...)
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('all');

  // Compute distinct sub-category labels if present in cell data
  const availableSubCategories = useMemo(() => {
    const cats = new Map<string, string>();
    for (const r of rows) {
      const label = r.cells.categoryLabel || r.cells.category || r.cells.parentCategory || r.cells.sourceTable;
      if (label) {
        cats.set(String(label), String(label));
      }
    }
    return Array.from(cats.values()).sort();
  }, [rows]);

  // Server already applied search / column Filters / Query. Keep only page-local sub-category.
  const filteredRows = useMemo(() => {
    if (selectedSubCategory === 'all') return rows;
    return rows.filter(
      (r) =>
        getResolvedCellValue(r, 'categoryLabel') === selectedSubCategory ||
        getResolvedCellValue(r, 'category') === selectedSubCategory ||
        getResolvedCellValue(r, 'parentCategory') === selectedSubCategory ||
        getResolvedCellValue(r, 'sourceTable') === selectedSubCategory,
    );
  }, [rows, selectedSubCategory]);

  const handleToggleRow = useCallback(
    (legacyId: string) => {
      if (selectionType === 'all') {
        const nextExcluded = new Set(excludedIds);
        if (nextExcluded.has(legacyId)) nextExcluded.delete(legacyId);
        else nextExcluded.add(legacyId);
        onExcludedIdsChange?.(nextExcluded);
        return;
      }
      const next = new Set(selectedIds);
      if (next.has(legacyId)) next.delete(legacyId);
      else next.add(legacyId);
      onSelectionChange(next);
    },
    [selectionType, selectedIds, excludedIds, onSelectionChange, onExcludedIdsChange],
  );

  const selectableRows = useMemo(
    () => filteredRows.filter((r) => r.band !== 'blocked'),
    [filteredRows],
  );

  const isRowSelected = useCallback(
    (legacyId: string) => {
      if (selectionType === 'all') {
        return !excludedIds?.has(legacyId);
      }
      return selectedIds.has(legacyId);
    },
    [selectionType, selectedIds, excludedIds],
  );

  const allPageSelected =
    selectableRows.length > 0 &&
    (selectionType === 'all'
      ? selectableRows.every((r) => !excludedIds.has(r.legacyId))
      : selectableRows.every((r) => selectedIds.has(r.legacyId)));

  const hasClientOnlyFilters = selectedSubCategory !== 'all';

  const showDatasetSelect = shouldOfferDatasetSelect({
    pageFullySelected: allPageSelected,
    visibleSelectableCount: selectableRows.length,
    serverTotal: total,
    hasClientOnlyFilters,
  });

  const handleToggleAll = useCallback(() => {
    const selectableIds = selectableRows.map((r) => r.legacyId);
    const isAllCurrentSelected =
      selectableRows.length > 0 &&
      (selectionType === 'all'
        ? selectableRows.every((r) => !excludedIds.has(r.legacyId))
        : selectableRows.every((r) => selectedIds.has(r.legacyId)));

    // Page checkbox never means the full dataset — only the visible/filtered page.
    onSelectionTypeChange?.('explicit');
    onExcludedIdsChange?.(new Set());
    onSelectionChange(togglePageIds(selectedIds, selectableIds, isAllCurrentSelected));
  }, [
    selectableRows,
    selectedIds,
    selectionType,
    excludedIds,
    onSelectionChange,
    onSelectionTypeChange,
    onExcludedIdsChange,
  ]);

  const handleToggleDataset = useCallback(() => {
    if (selectionType === 'all') {
      onSelectionTypeChange?.('explicit');
      onExcludedIdsChange?.(new Set());
      onSelectionChange(new Set());
      return;
    }
    onSelectionTypeChange?.('all');
    onExcludedIdsChange?.(new Set());
    onSelectionChange(new Set());
  }, [selectionType, onSelectionTypeChange, onExcludedIdsChange, onSelectionChange]);

  if (isFetching && rows.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/80 text-sm text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
        <span>Loading legacy dataset records…</span>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <XCircle className="h-8 w-8 text-red-500" />
        <p className="text-sm font-medium text-red-700">{fetchError}</p>
        <button
          onClick={refetch}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-xs"
        >
          <RefreshCw className="h-3.5 w-3.5 text-gray-600" />
          Retry
        </button>
      </div>
    );
  }

  // Pagination calculations
  const showingStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingEnd = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < pageCount;

  // Page buttons
  const pageButtons: number[] = [];
  const maxVisible = 7;
  if (pageCount <= maxVisible) {
    for (let i = 1; i <= pageCount; i++) pageButtons.push(i);
  } else if (page <= 4) {
    for (let i = 1; i <= maxVisible; i++) pageButtons.push(i);
  } else if (page >= pageCount - 3) {
    for (let i = pageCount - maxVisible + 1; i <= pageCount; i++) pageButtons.push(i);
  } else {
    for (let i = page - 3; i <= page + 3; i++) pageButtons.push(i);
  }

  const activeColFilterCount = Object.values(colFilters).filter(Boolean).length;
  const activeQueryCount = queryGroup.conditions.length;
  const hasActiveFilters = Boolean(
    searchQuery?.trim() ||
    (bandFilter && bandFilter !== 'all') ||
    selectedSubCategory !== 'all' ||
    activeColFilterCount > 0 ||
    activeQueryCount > 0
  );

  const resetFilters = () => {
    onSearchChange?.('');
    onBandFilterChange?.('all');
    setSelectedSubCategory('all');
    setColFilters({});
    setQueryGroup(emptyGroup());
  };

  return (
    <div className="flex flex-col min-h-0 h-full relative">
      {isFetching && rows.length > 0 && (
        <div className="absolute inset-0 z-20 bg-white/70 flex items-center justify-center pointer-events-auto">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
            <span>Loading records…</span>
          </div>
        </div>
      )}
      {/* Platform Standard DataGrid Toolbar (Image 2 Parity) */}
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b bg-white border-gray-200">
        {/* Left: Search input box & Sub-Category Selector */}
        <div className="flex items-center gap-2 flex-1 max-w-lg">
          {onSearchChange && (
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search code, description, category, ID…"
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm bg-white font-normal focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Sub-Category Selector (e.g., Backup Power, Fire Alarm, Fire Pump...) */}
          {availableSubCategories.length > 1 && (
            <select
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              className="px-2.5 py-2 text-xs border border-gray-200 rounded-lg bg-white font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 shadow-xs shrink-0"
            >
              <option value="all">All Sub-Categories ({total.toLocaleString()})</option>
              {availableSubCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Right: Standard Toolbar Buttons (Filters, Query, Column Settings, Export) */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Filters Button — Toggles Column Filter Row */}
          <button
            type="button"
            onClick={() => setShowFilterRow((p) => !p)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm font-medium transition-colors shadow-xs ${
              showFilterRow || activeColFilterCount > 0 || (bandFilter && bandFilter !== 'all')
                ? 'bg-orange-50 text-orange-700 border-orange-300 font-semibold'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal className="h-4 w-4 text-orange-600" />
            <span>Filters</span>
            {activeColFilterCount > 0 && (
              <span className="px-1.5 py-0.2 text-[10px] font-bold bg-orange-200 text-orange-900 rounded-full">
                {activeColFilterCount}
              </span>
            )}
          </button>

          {/* Query Button — Opens Query Builder Modal */}
          <button
            type="button"
            onClick={() => setShowQueryModal(true)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm font-medium transition-colors shadow-xs ${
              activeQueryCount > 0
                ? 'bg-orange-50 text-orange-700 border-orange-300 font-semibold'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Zap className="h-4 w-4 text-orange-500 fill-orange-500" />
            <span>Query</span>
            {activeQueryCount > 0 && (
              <span className="px-1.5 py-0.2 text-[10px] font-bold bg-orange-200 text-orange-900 rounded-full">
                {activeQueryCount}
              </span>
            )}
          </button>

          {/* Column Settings Button */}
          <div className="relative" ref={colSettingsRef}>
            <button
              type="button"
              onClick={() => setShowColumnSettingsPopover((p) => !p)}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-xs"
            >
              <Settings2 className="h-4 w-4 text-gray-500" />
              <span>Column Settings</span>
              <span className="px-1.5 py-0.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded-full">
                {visibleColumns.length}
              </span>
            </button>

            {showColumnSettingsPopover && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-30 p-3 text-xs space-y-2">
                <div className="flex items-center justify-between font-semibold text-gray-800 pb-1 border-b border-gray-100">
                  <span>Toggle Columns</span>
                  <button
                    type="button"
                    onClick={() => setHiddenColumnKeys(new Set())}
                    className="text-[10px] text-orange-700 hover:underline"
                  >
                    Reset All
                  </button>
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {columns.map((col) => {
                    const isVisible = !hiddenColumnKeys.has(col.key);
                    return (
                      <button
                        key={col.key}
                        type="button"
                        onClick={() => toggleColumnVisibility(col.key)}
                        className="w-full flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50 text-left text-gray-700"
                      >
                        <span className="truncate">{col.header}</span>
                        {isVisible ? (
                          <Eye className="h-3.5 w-3.5 text-orange-600 shrink-0" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {onToggleFullscreen && (
          <button
            type="button"
            onClick={onToggleFullscreen}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-xs"
            title={isFullscreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4 text-gray-500" /> : <Maximize2 className="h-4 w-4 text-gray-500" />}
            <span>{isFullscreen ? 'Exit Full Screen' : 'Full Screen'}</span>
          </button>
          )}

          {/* Export Button */}
          <div className="relative" ref={exportRef}>
            <button
              type="button"
              onClick={() => setShowExportPopover((p) => !p)}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-xs"
            >
              <Download className="h-4 w-4 text-gray-500" />
              <span>Export</span>
            </button>

            {showExportPopover && (
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-30 p-1.5 text-xs space-y-0.5">
                <button
                  type="button"
                  onClick={() => {
                    exportToCSV(`${datasetKey}-export`, columns, filteredRows);
                    setShowExportPopover(false);
                  }}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                  <span>Export CSV</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    exportToJSON(`${datasetKey}-export`, filteredRows);
                    setShowExportPopover(false);
                  }}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span>Export JSON</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {(showDatasetSelect || selectionType === 'all') && (
        <div className="shrink-0 flex items-center gap-2 px-4 py-1.5 border-b bg-orange-50 border-orange-100 text-xs text-orange-900">
          <input
            type="checkbox"
            checked={selectionType === 'all'}
            onChange={handleToggleDataset}
            className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
            aria-label={`Select all ${total} records`}
          />
          <span>
            {selectionType === 'all'
              ? `All ${total} records are selected.`
              : `All ${selectableRows.length} records on this page are selected.`}
          </span>
          {selectionType === 'all' ? (
            <button
              type="button"
              onClick={handleToggleDataset}
              className="font-semibold text-orange-700 hover:underline"
            >
              Clear selection
            </button>
          ) : (
            <button
              type="button"
              onClick={handleToggleDataset}
              className="font-semibold text-orange-700 hover:underline"
            >
              Select all {total} records
            </button>
          )}
        </div>
      )}

      {/* Grid table — scrollable area */}
      <div className="overflow-auto flex-1 min-h-0 relative bg-white">
        {filteredRows.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 px-4 text-center">
            <Search className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-gray-700 font-medium text-sm">
              {hasActiveFilters ? 'No legacy records match your search or filter' : 'No legacy records found for this department and dataset.'}
            </p>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              {hasActiveFilters
                ? 'Try adjusting your search query or clearing column filters.'
                : 'Please click Refresh or verify your tenant SQL database connection.'}
            </p>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors shadow-xs"
              >
                <X className="h-3.5 w-3.5" />
                Reset Search & Filters
              </button>
            ) : (
              <button
                type="button"
                onClick={refetch}
                className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-xs"
              >
                <RefreshCw className="h-3.5 w-3.5 text-gray-500" />
                Refresh
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              {/* Main Column Header Row */}
              <tr className="border-b bg-gray-50/80 border-gray-200">
                <th className="w-10 px-3.5 py-2.5 text-left">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={handleToggleAll}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    aria-label="Select all records on this page"
                    title={`Select all ${selectableRows.length} records on this page`}
                  />
                </th>
                <th
                  onClick={() => onSortChange?.('score')}
                  className={`px-3.5 py-2.5 text-left font-semibold text-xs uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors ${
                    sortBy === 'score' ? 'text-orange-700 font-bold' : 'text-gray-500'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <span>Score</span>
                    {sortBy === 'score' ? (
                      sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-30" />
                    )}
                  </div>
                </th>
                {visibleColumns.map((col) => {
                  const isSorted = sortBy === col.key;
                  return (
                    <th
                      key={col.key}
                      onClick={() => onSortChange?.(col.key)}
                      className={`px-3.5 py-2.5 text-left font-semibold text-xs uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors ${
                        isSorted ? 'text-orange-700 font-bold' : 'text-gray-500'
                      }`}
                      style={{ minWidth: col.width }}
                    >
                      <div className="flex items-center gap-1">
                        <span>{col.header}</span>
                        {isSorted ? (
                          sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-30" />
                        )}
                      </div>
                    </th>
                  );
                })}
                <th className="px-3.5 py-2.5 text-left font-semibold text-xs uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-3.5 py-2.5 text-left font-semibold text-xs uppercase tracking-wider text-gray-500">Actions</th>
              </tr>

              {/* Standard Platform Column Filter Inputs Row (Image 2 Parity) */}
              {showFilterRow && (
                <tr className="border-b bg-orange-50/40 border-orange-200">
                  <th className="px-3 py-1.5 text-center">
                    {activeColFilterCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setColFilters({})}
                        className="p-1 text-orange-600 hover:text-orange-800 transition-colors"
                        title="Clear column filters"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </th>
                  <th className="px-2 py-1.5">
                    <select
                      value={bandFilter}
                      onChange={(e) => onBandFilterChange?.(e.target.value)}
                      className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded bg-white font-medium focus:ring-1 focus:ring-orange-500"
                    >
                      <option value="all">All</option>
                      <option value="green">Green</option>
                      <option value="yellow">Yellow</option>
                      <option value="red">Red</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </th>
                  {visibleColumns.map((col) => (
                    <th key={col.key} className="px-2 py-1.5">
                      <input
                        type="text"
                        value={colFilters[col.key] || ''}
                        onChange={(e) =>
                          setColFilters({ ...colFilters, [col.key]: e.target.value })
                        }
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white font-normal text-gray-800 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    </th>
                  ))}
                  <th className="px-2 py-1.5" />
                  <th className="px-2 py-1.5" />
                </tr>
              )}
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const isMigrated = !!row.migratedReceipt;
                const isBlocked = row.band === 'blocked';
                const isDisabled = isBlocked;
                const isSelected = isRowSelected(row.legacyId);
                return (
                  <tr
                    key={row.legacyId}
                    className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors"
                    style={{ opacity: isDisabled ? 0.6 : 1 }}
                  >
                    <td className="px-3.5 py-2.5">
                      <input
                        type="checkbox"
                        checked={isSelected && !isDisabled}
                        disabled={isDisabled}
                        onChange={() => !isDisabled && handleToggleRow(row.legacyId)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        aria-label={`Select row ${row.legacyId}`}
                      />
                    </td>
                    <td className="px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <BandBadge band={row.band} />
                        <span className="tabular-nums text-xs text-gray-500">{row.score}</span>
                      </div>
                    </td>
                    {visibleColumns.map((col) => {
                      if (col.key === 'health') {
                        const sourceUrl = row.cells.sourceUrl;
                        return (
                          <td key={col.key} className="px-3.5 py-2.5" style={{ minWidth: col.width }}>
                            <DocumentHealthCheckButton
                              sourceUrl={typeof sourceUrl === 'string' ? sourceUrl : null}
                            />
                          </td>
                        );
                      }
                      const displayVal = getResolvedCellValue(row, col.key);
                      return (
                        <td key={col.key} className="px-3.5 py-2.5 font-mono text-xs text-gray-800" style={{ minWidth: col.width }}>
                          {displayVal ? displayVal : <span className="text-gray-300">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-3.5 py-2.5">
                      {isMigrated ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200" title="Already migrated. Re-select to remigrate.">
                          <CheckCircle2 className="h-3 w-3" />
                          Migrated
                        </span>
                      ) : row.issues.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setViewRow(row)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full cursor-pointer transition-colors shadow-xs"
                          title={`${row.issues.map((i) => i.message).join('; ')} — Click to view details`}
                        >
                          <AlertTriangle className="h-3 w-3 text-amber-600" />
                          <span>{row.issues.length} {row.issues.length === 1 ? 'issue' : 'issues'}</span>
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-3.5 py-2.5">
                      <button
                        type="button"
                        onClick={() => setViewRow(row)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors shadow-xs"
                      >
                        <Eye className="h-3 w-3 text-gray-500" />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Standard Platform DataGrid Pagination Footer */}
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-t bg-white border-gray-200 text-xs text-gray-600">
        <div className="flex items-center gap-4">
          <span>
            Showing <strong className="font-semibold text-gray-900">{showingStart.toLocaleString()}</strong>–
            <strong className="font-semibold text-gray-900">{showingEnd.toLocaleString()}</strong> of{' '}
            <strong className="font-semibold text-gray-900">{total.toLocaleString()}</strong> records
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500">Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1);
              }}
              className="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white font-medium focus:ring-1 focus:ring-orange-500"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Page Navigation Buttons */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={!canPrev}
            className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors"
            title="First Page"
          >
            <ChevronsLeft className="h-3.5 w-3.5 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={!canPrev}
            className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors"
            title="Previous Page"
          >
            <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
          </button>

          {pageButtons.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                p === page
                  ? 'bg-orange-600 text-white border-orange-600 shadow-xs'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {p}
            </button>
          ))}

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={!canNext}
            className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors"
            title="Next Page"
          >
            <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(pageCount)}
            disabled={!canNext}
            className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors"
            title="Last Page"
          >
            <ChevronsRight className="h-3.5 w-3.5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Query Builder Modal */}
      {showQueryModal && (
        <QueryBuilderModal
          columns={visibleColumns}
          query={queryGroup}
          onApply={(q) => setQueryGroup(q)}
          onClose={() => setShowQueryModal(false)}
        />
      )}

      {/* Row detail review modal */}
      {viewRow && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2.5">
                <span className="font-bold text-base text-gray-900">Row {viewRow.legacyId}</span>
                <BandBadge band={viewRow.band} />
                <span className="text-xs text-gray-500 font-mono">Score: {viewRow.score}</span>
              </div>
              <button
                type="button"
                onClick={() => setViewRow(null)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4 text-xs">
              {viewRow.issues.length > 0 && (
                <div>
                  <h4 className="font-semibold text-amber-900 mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                    Compatibility & Validation Issues ({viewRow.issues.length})
                  </h4>
                  <ul className="space-y-1 bg-amber-50/80 border border-amber-200 rounded-lg p-3 text-amber-900">
                    {viewRow.issues.map((iss, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="font-bold shrink-0">•</span>
                        <span>{iss.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-gray-800 mb-1.5">Raw Extract Cells</h4>
                <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-[11px] font-mono leading-relaxed">
                  {JSON.stringify(viewRow.cells, null, 2)}
                </pre>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex justify-end">
              <button
                type="button"
                onClick={() => setViewRow(null)}
                className="px-4 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
